import { Button, cn, DEFAULT_TEMPLATE_ID, FullCVWithTemplate, TemplateId } from '@cibli/shared';
import { AlertTriangle, ArrowDown, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import ConfirmDialog from '@/features/common/components/ConfirmDialog';
import { CloseTypeEnum } from '@/features/common/components/Header';
import MainLayout from '@/features/common/components/MainLayout';
import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { useCvThemes } from '@/features/cv-theme-picker/use-cv-themes';
import { useFeedbackContext } from '@/features/feedback';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { API_BASE } from '@/lib/api-url';
import { sendCVBySMS, sendCVByEmail, updateUserEmail, fetchCV } from '@/services/interview';

import EmailInputDialog from '../components/EmailInputDialog';
import PageDelimiter from '../components/PageDelimiter';
import TemplateSkeleton from '../components/TemplateSkeleton';
import { colorsPalette } from '../templates/colors.constant';
import { PASTEL_COLORS } from '../templates/helpers';
import { getTemplate, listTemplates } from '../templates/registry';
import TemplateHost from '../templates/TemplateHost';
import { getPageHeightInPx } from '../utils/page-utils';
import CVPrintingScreen from './CVPrintingScreen';

interface Props {
  cvId: string;
  onContinue: () => void;
  onCancel: () => void;
  onBackToEditor: () => void;
  onValidate: (templateId: TemplateId, params: Record<string, unknown>) => Promise<void>;
  userEmail?: string | null;
  userPhone?: string | null;
  statusBar?: React.ReactNode;
  onPhaseChange?: (phase: 'appearance' | 'reception') => void;
}

const vividColors = colorsPalette.filter(color => !PASTEL_COLORS.has(color));
const pastelColors = colorsPalette.filter(color => PASTEL_COLORS.has(color));

function templateIdToThemeId(templateId: TemplateId): string {
  if (templateId.startsWith('json-')) {
    return templateId.replace('json-', '');
  }
  return templateId;
}

function isTemplateId(value: string): value is TemplateId {
  return listTemplates().some(template => template.id === value);
}

function isParamsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getTemplateParams(
  templateId: TemplateId,
  contextParams: Record<string, unknown>,
  serverParams: unknown,
): Record<string, unknown> {
  const template = getTemplate(templateId);
  const candidate =
    Object.keys(contextParams).length > 0
      ? contextParams
      : isParamsRecord(serverParams)
        ? serverParams
        : template.defaults;
  const parsed = template.schema.safeParse(candidate);
  return parsed.success ? parsed.data : template.defaults;
}

function logSendError(channel: 'sms' | 'email', error: unknown): void {
  const e = error as {
    name?: string;
    code?: string;
    message?: string;
    response?: { status?: number; data?: unknown };
  };
  console.error(`CVReception ${channel} send failed`, {
    name: e?.name,
    code: e?.code,
    message: e?.message,
    status: e?.response?.status,
    data: e?.response?.data,
  });
}

export default function CVReceptionScreen({
  cvId,
  onContinue,
  onCancel,
  onBackToEditor,
  onValidate,
  userEmail,
  userPhone,
  statusBar,
  onPhaseChange,
}: Props) {
  const { t } = useTranslation();
  const { cvTemplateId, cvTemplateParams, setCvTemplateId, setCvTemplateParams } =
    useCVTemplateContext();
  const { data: enabledThemes } = useCvThemes();
  const { printerAvailable, printFromURL } = useBooth();
  const { isOpen: isFeedbackOpen } = useFeedbackContext();
  const [previewCV, setPreviewCV] = useState<FullCVWithTemplate | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [customizationValidated, setCustomizationValidated] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<TemplateId>(cvTemplateId);
  const [selectedParams, setSelectedParams] = useState<Record<string, unknown>>(cvTemplateParams);

  const availableTemplates = useMemo(() => {
    const all = listTemplates();
    if (!enabledThemes) return all;
    const themeMap = new Map(enabledThemes.map((t, idx) => [t.id, t.position ?? idx]));
    const filtered = all.filter(tpl => themeMap.has(templateIdToThemeId(tpl.id)));
    if (filtered.length === 0) return all;
    return filtered.sort((a, b) => {
      const posA = themeMap.get(templateIdToThemeId(a.id)) ?? 999;
      const posB = themeMap.get(templateIdToThemeId(b.id)) ?? 999;
      return posA - posB;
    });
  }, [enabledThemes]);

  // If the currently selected template was disabled in BO, switch to the first enabled one
  useEffect(() => {
    if (!enabledThemes || availableTemplates.length === 0) return;
    const isCurrentAvailable = availableTemplates.some(tpl => tpl.id === selectedTemplateId);
    if (!isCurrentAvailable) {
      const fallbackId = availableTemplates[0].id;
      const template = getTemplate(fallbackId);
      const nextParams = {
        ...template.defaults,
        ...(typeof selectedParams.accentColor === 'string'
          ? { accentColor: selectedParams.accentColor }
          : {}),
      };
      setSelectedTemplateId(fallbackId);
      setSelectedParams(nextParams);
      setCvTemplateId(fallbackId);
      setCvTemplateParams(nextParams);
    }
  }, [
    availableTemplates,
    enabledThemes,
    selectedParams.accentColor,
    selectedTemplateId,
    setCvTemplateId,
    setCvTemplateParams,
  ]);
  const [isValidating, setIsValidating] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [printSent, setPrintSent] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const cvScrollRef = useRef<HTMLDivElement | null>(null);
  const cvPageRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);

  // Show the scroll-down arrow whenever the CV overflows the preview container
  const updateHasMoreContent = useCallback(() => {
    const el = cvScrollRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight > el.clientHeight + 30;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
    setHasMoreContent(scrollable && !atBottom);
  }, []);

  useEffect(() => {
    updateHasMoreContent();
    const content = cvScrollRef.current?.firstElementChild;
    if (!content) return;
    const ro = new ResizeObserver(() => updateHasMoreContent());
    ro.observe(content);
    return () => ro.disconnect();
  }, [
    previewCV,
    selectedTemplateId,
    selectedParams,
    isPreviewLoading,
    customizationValidated,
    updateHasMoreContent,
  ]);

  // Notify parent of phase changes
  useEffect(() => {
    onPhaseChange?.(customizationValidated ? 'reception' : 'appearance');
  }, [customizationValidated, onPhaseChange]);
  const [cvPageCount, setCvPageCount] = useState(1);

  // Set body background to white for this page (overrides default gray bg-background)
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#ffffff';
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    setIsPreviewLoading(true);
    setPreviewError(null);

    void fetchCV(cvId)
      .then(data => {
        if (!isActive) return;
        const rawTemplateId = data.template || cvTemplateId || DEFAULT_TEMPLATE_ID;
        const nextTemplateId = isTemplateId(rawTemplateId) ? rawTemplateId : DEFAULT_TEMPLATE_ID;
        const nextParams = getTemplateParams(nextTemplateId, cvTemplateParams, data.templateParams);
        setPreviewCV(data);
        setSelectedTemplateId(nextTemplateId);
        setSelectedParams(nextParams);
        setCvTemplateId(nextTemplateId);
        setCvTemplateParams(nextParams);
      })
      .catch(error => {
        console.error('Error fetching CV reception preview:', error);
        if (isActive) setPreviewError('Impossible de charger votre aperçu. Veuillez réessayer.');
      })
      .finally(() => {
        if (isActive) setIsPreviewLoading(false);
      });

    return () => {
      isActive = false;
    };
    // The preview is fetched once per CV. Context updates below must not refetch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId]);

  const handleColorSelect = useCallback(
    (color: string) => {
      const nextParams = { ...selectedParams, accentColor: color };
      setSelectedParams(nextParams);
      setCvTemplateParams(nextParams);
    },
    [selectedParams, setCvTemplateParams],
  );

  const handleTemplateSelect = useCallback(
    (templateId: TemplateId) => {
      const template = getTemplate(templateId);
      const nextParams = {
        ...template.defaults,
        ...(typeof selectedParams.accentColor === 'string'
          ? { accentColor: selectedParams.accentColor }
          : {}),
      };
      setSelectedTemplateId(templateId);
      setSelectedParams(nextParams);
      setCvTemplateId(templateId);
      setCvTemplateParams(nextParams);
    },
    [selectedParams.accentColor, setCvTemplateId, setCvTemplateParams],
  );

  const handleValidateCustomization = useCallback(async () => {
    if (!previewCV || isValidating) return;
    setIsValidating(true);
    try {
      await onValidate(selectedTemplateId, selectedParams);
      setCustomizationValidated(true);
    } catch (error) {
      console.error('Error validating CV customization:', error);
      toast.error('Impossible d\u2019enregistrer votre CV. Veuillez r\u00e9essayer.');
    } finally {
      setIsValidating(false);
    }
  }, [isValidating, onValidate, previewCV, selectedParams, selectedTemplateId]);

  // Detect page overflow: observe the CV content height vs A4 page height
  useEffect(() => {
    const el = cvPageRef.current;
    if (!el) return;
    const pageHeightPx = getPageHeightInPx();
    const PAGE_OVERFLOW_TOLERANCE_PX = 20;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height;
      if (h)
        setCvPageCount(
          Math.max(1, Math.ceil(Math.max(0, h - PAGE_OVERFLOW_TOLERANCE_PX) / pageHeightPx)),
        );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [previewCV, selectedTemplateId, selectedParams]);

  const handleSendSMS = useCallback(async () => {
    if (smsSent || isSendingSMS) return;
    setIsSendingSMS(true);
    try {
      await sendCVBySMS(cvId, { showErrorToast: false });
      setSmsSent(true);
      toast.success(t('CVReception.smsSuccess'));
    } catch (error) {
      logSendError('sms', error);
      toast.error(t('CVReception.smsError'));
    } finally {
      setIsSendingSMS(false);
    }
  }, [cvId, smsSent, isSendingSMS, t]);

  const handleSendEmail = useCallback(async () => {
    if (emailSent || isSendingEmail) return;
    if (!userEmail) {
      setShowEmailDialog(true);
      return;
    }

    setIsSendingEmail(true);
    try {
      await sendCVByEmail(cvId, { showErrorToast: false, email: userEmail });
      setEmailSent(true);
      toast.success(t('CVReception.emailSuccess'));
    } catch (error) {
      logSendError('email', error);
      toast.error(t('CVReception.emailError'));
    } finally {
      setIsSendingEmail(false);
    }
  }, [cvId, emailSent, isSendingEmail, userEmail, t]);

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      setIsUpdatingEmail(true);
      try {
        await updateUserEmail(email);
        await sendCVByEmail(cvId, { showErrorToast: false, email });
        setEmailSent(true);
        toast.success(t('CVReception.emailSuccess'));
        setShowEmailDialog(false);
      } catch (error: unknown) {
        logSendError('email', error);
        toast.error(t('CVReception.emailError'));
      } finally {
        setIsUpdatingEmail(false);
      }
    },
    [cvId, t],
  );

  const handlePrint = useCallback(async () => {
    if (!printerAvailable || printSent || printing || isPrinting) {
      if (!printerAvailable) onContinue();
      return;
    }
    setIsPrinting(true);
    setPrinting(true);
    try {
      const url = new URL(`${API_BASE}/cv/${cvId}/download`);
      const printed = await printFromURL(url.toString());
      if (!printed) {
        throw new Error('print failed');
      }
      setPrintSent(true);
      toast.success(t('CVReception.printSuccess'));
    } catch (error) {
      console.error(error);
      toast.error(t('CVReception.printError'));
      setPrinting(false);
      setIsPrinting(false);
      return;
    }
  }, [cvId, onContinue, printFromURL, printerAvailable, printSent, printing, isPrinting, t]);

  const handlePrintingDone = useCallback(() => {
    setPrinting(false);
    setIsPrinting(false);
  }, []);

  const handleContinueClick = useCallback(() => {
    if (!smsSent && !emailSent && !printSent) {
      setShowConfirmDialog(true);
    } else {
      onContinue();
    }
  }, [emailSent, onContinue, printSent, smsSent]);

  const selectedColor =
    typeof selectedParams.accentColor === 'string' ? selectedParams.accentColor : colorsPalette[0];

  if (printing) {
    return <CVPrintingScreen onContinue={handlePrintingDone} />;
  }

  if (!customizationValidated) {
    return (
      <MainLayout
        align="top-center"
        closeType={CloseTypeEnum.End}
        rightContent={
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              className="h-14 rounded-xl text-base font-bold px-5"
              onClick={onBackToEditor}
            >
              <ArrowLeft className="mr-2 size-5" aria-hidden="true" />
              {t('CVReception.back')}
            </Button>
            <Button
              type="button"
              className="h-14 rounded-xl bg-[#21c294] text-base font-bold text-white px-5 shadow-[0_2px_8px_rgba(33,194,148,0.2)] hover:bg-[#009f7f]"
              disabled={!previewCV || isPreviewLoading || Boolean(previewError) || isValidating}
              onClick={() => void handleValidateCustomization()}
            >
              {isValidating && <Loader2 className="mr-2 size-5 animate-spin" aria-hidden="true" />}
              {t('dashboard.cvEdit.validate')}
            </Button>
          </div>
        }
        className="!min-h-0 h-[150dvh] max-h-[1920px] bg-white"
        headerClassName="bg-white/95"
        logoContainerClassName="bg-transparent p-0"
        padding="px-0 pb-0 pt-4 sm:px-0 sm:pb-0 sm:pt-6"
        onClose={onCancel}
      >
        <h1 className="text-xl font-bold text-[#254441] text-center sm:text-2xl">
          {t('CVReception.customize.title')}
        </h1>
        {statusBar}
        <main className="relative flex flex-col w-full max-w-4xl mx-auto h-full text-black">
          {cvPageCount > 1 && (
            <div className="mt-2 inline-flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 text-xs sm:text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                {t(
                  'CV.pageOverflowWarning',
                  'Attention : votre CV fait {{pageCount}} page(s). Réduisez le contenu pour un rendu optimal.',
                  { pageCount: cvPageCount },
                )}
              </span>
            </div>
          )}

          {/* CV A4 preview — fixed height, scroll ONLY inside this container */}
          <section
            aria-labelledby="cv-preview-title"
            className="relative mx-3 shrink-0 overflow-x-hidden"
            style={{ height: 'min(68dvh, 700px)' }}
          >
            <h2 id="cv-preview-title" className="sr-only">
              {t('CVReception.customize.previewTitle')}
            </h2>
            <div
              className="h-full overflow-y-auto overflow-x-hidden rounded-lg"
              ref={cvScrollRef}
              onScroll={updateHasMoreContent}
            >
              <div className="mx-auto w-[210mm] min-w-[210mm] py-4">
                {isPreviewLoading && (
                  <div
                    className="flex items-center justify-center aspect-[210/297] text-[#254441] bg-[#f0f0f0] rounded-lg"
                    role="status"
                  >
                    <Loader2 className="size-8 animate-spin" aria-hidden="true" />
                    <span className="sr-only">{t('CVReception.customize.previewLoading')}</span>
                  </div>
                )}
                {previewError && <p className="text-center text-[#571426] py-8">{previewError}</p>}
                {previewCV && !isPreviewLoading && !previewError && (
                  <div
                    ref={cvPageRef}
                    className="relative bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] rounded-lg"
                  >
                    <TemplateHost
                      cv={previewCV}
                      mode="preview"
                      templateId={selectedTemplateId}
                      initialParams={selectedParams}
                    />
                    <PageDelimiter containerRef={cvPageRef} />
                  </div>
                )}
              </div>
            </div>
            {hasMoreContent && !isFeedbackOpen && (
              <button
                type="button"
                onClick={() => {
                  const el = cvScrollRef.current;
                  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                }}
                className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 animate-bounce rounded-full bg-[#21c294] p-2 shadow-lg transition-transform hover:scale-110 active:scale-95"
                aria-label={t('CVReception.customize.scrollAria')}
              >
                <ArrowDown className="size-6 text-white" strokeWidth={3} />
              </button>
            )}
          </section>

          {/* Panels + buttons — scrollable page */}
          <section aria-labelledby="appearance-title" className="relative px-3 mt-4 pb-4">
            <h2 id="appearance-title" className="sr-only">
              {t('CVReception.customize.appearanceTitle')}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
              <fieldset className="relative rounded-xl border border-[#e8e8e8] bg-gradient-to-br from-[#fbf7f5] to-white p-4 shadow-sm">
                <legend className="sr-only">{t('CVReception.customize.colorTitle')}</legend>
                <h3 className="text-base font-bold leading-6 text-[#254441]">
                  {t('CVReception.customize.colorTitle')}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {t('CV.TemplateConfigureDropdown.chooseColor')}
                </p>
                <div className="mt-3 space-y-3">
                  <section aria-labelledby="vivid-colors-title">
                    <h3
                      id="vivid-colors-title"
                      className="text-xs font-semibold uppercase tracking-wide text-gray-400"
                    >
                      {t('CV.ColorPalette.vividColors')}
                    </h3>
                    <div className="mt-2 grid grid-cols-5 gap-3 min-[420px]:grid-cols-6 sm:gap-4">
                      {vividColors.map(color => {
                        const isSelected = selectedColor === color;
                        return (
                          <button
                            key={color}
                            type="button"
                            aria-label={
                              isSelected
                                ? t('CVReception.customize.colorSelectedAria', { color })
                                : t('CVReception.customize.colorAria', { color })
                            }
                            aria-pressed={isSelected}
                            className={cn(
                              'min-h-11 min-w-11 rounded-xl border-2 border-transparent shadow-sm transition-all hover:scale-105',
                              isSelected &&
                                'scale-105 border-2 border-[#21c294] shadow-[0_0_0_3px_rgba(33,194,148,0.3)]',
                            )}
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorSelect(color)}
                          />
                        );
                      })}
                    </div>
                  </section>
                  <section aria-labelledby="pastel-colors-title">
                    <h3
                      id="pastel-colors-title"
                      className="text-xs font-semibold uppercase tracking-wide text-gray-400"
                    >
                      {t('CV.ColorPalette.pastelColors')}
                    </h3>
                    <div className="mt-2 grid grid-cols-5 gap-3 min-[420px]:grid-cols-6 sm:gap-4">
                      {pastelColors.map(color => {
                        const isSelected = selectedColor === color;
                        return (
                          <button
                            key={color}
                            type="button"
                            aria-label={
                              isSelected
                                ? t('CVReception.customize.colorSelectedAria', { color })
                                : t('CVReception.customize.colorAria', { color })
                            }
                            aria-pressed={isSelected}
                            className={cn(
                              'min-h-11 min-w-11 rounded-xl border-2 border-transparent shadow-sm transition-all hover:scale-105',
                              isSelected &&
                                'scale-105 border-2 border-[#21c294] shadow-[0_0_0_3px_rgba(33,194,148,0.3)]',
                            )}
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorSelect(color)}
                          />
                        );
                      })}
                    </div>
                  </section>
                </div>
              </fieldset>

              <fieldset className="relative rounded-xl border border-[#d6e8f7] bg-gradient-to-br from-[#f0f7ff] to-white p-4 shadow-sm">
                <legend className="sr-only">{t('CVReception.customize.templateTitle')}</legend>
                <h3 className="text-base font-bold leading-6 text-[#254441]">
                  {t('CVReception.customize.templateTitle')}
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {t('CV.TemplateSelect.chooseTemplate')}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-3">
                  {availableTemplates.map(choice => {
                    const templateId = choice.id;
                    const isSelected = selectedTemplateId === templateId;
                    return (
                      <button
                        key={templateId}
                        type="button"
                        aria-label={
                          isSelected
                            ? t('CVReception.customize.templateSelectedAria', {
                                template: choice.name,
                              })
                            : t('CVReception.customize.templateAria', { template: choice.name })
                        }
                        aria-pressed={isSelected}
                        className={cn(
                          'flex min-h-36 min-w-0 flex-col items-center justify-between gap-2 overflow-hidden rounded-lg border border-[#e8e8e8] bg-white p-2 text-center transition-all hover:shadow-md sm:min-h-40',
                          isSelected &&
                            'border-2 border-[#21c294] shadow-[0_0_0_3px_rgba(33,194,148,0.2)]',
                        )}
                        onClick={() => handleTemplateSelect(templateId)}
                      >
                        <TemplateSkeleton
                          templateId={templateId}
                          accentColor={selectedColor}
                          isSelected={false}
                        />
                        <strong className="whitespace-nowrap text-[10px] leading-tight text-[#555] sm:text-xs">
                          {choice.name}
                        </strong>
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          </section>
        </main>
      </MainLayout>
    );
  }

  if (!previewCV) return null;

  const canSendSMS = Boolean(userPhone);
  const canSendEmail = true;

  return (
    <MainLayout
      align="top-center"
      closeType={CloseTypeEnum.End}
      className="gap-6 sm:gap-8"
      headerClassName="bg-white/95"
      logoContainerClassName="bg-transparent p-0"
      padding="pt-4 pb-[120px] sm:pt-6"
      onClose={onCancel}
    >
      <p className="px-4 text-center text-xl font-bold text-[#254441] sm:text-2xl">
        {t('CVReception.subtitle')}
      </p>
      {statusBar}
      <div className="flex max-h-[50vh] w-full justify-center overflow-x-hidden overflow-y-auto">
        <div className="origin-top scale-[0.6] w-[794px]">
          <TemplateHost
            cv={previewCV as FullCVWithTemplate}
            mode="preview"
            templateId={selectedTemplateId}
            initialParams={selectedParams}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 px-4">
        {canSendSMS && (
          <Button
            size="lg"
            variant={smsSent ? 'secondary' : 'default'}
            onClick={handleSendSMS}
            disabled={smsSent || isSendingSMS}
            className={cn('relative', smsSent && 'opacity-60')}
          >
            {t('CVReception.sendSMS')}
            {smsSent && (
              <span className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-primary/20">
                <CheckCircle2 className="size-4 text-primary" />
              </span>
            )}
          </Button>
        )}
        {canSendEmail && (
          <Button
            size="lg"
            variant={emailSent ? 'secondary' : 'default'}
            onClick={handleSendEmail}
            disabled={emailSent || isSendingEmail}
            className={cn('relative', emailSent && 'opacity-60')}
          >
            {t('CVReception.sendEmail')}
            {emailSent && (
              <span className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-primary/20">
                <CheckCircle2 className="size-4 text-primary" />
              </span>
            )}
          </Button>
        )}
        {printerAvailable && (
          <Button
            size="lg"
            variant={printSent ? 'secondary' : 'default'}
            onClick={handlePrint}
            disabled={printSent || printing || isPrinting}
            className={cn('relative', printSent && 'opacity-60')}
          >
            {t('CVReception.print')}
            {printSent && (
              <span className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-primary/20">
                <CheckCircle2 className="size-4 text-primary" />
              </span>
            )}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 px-4">
        <Button variant="outline" size="lg" onClick={() => setCustomizationValidated(false)}>
          <ArrowLeft className="mr-2 size-5" />
          {t('CVReception.back')}
        </Button>
        <Button variant="secondary" size="lg" onClick={handleContinueClick}>
          {t('CVReception.skip')}
        </Button>
        <Button size="lg" onClick={handleContinueClick}>
          {t('CVReception.viewJobs')}
        </Button>
      </div>

      <EmailInputDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        onSubmit={handleEmailSubmit}
        isLoading={isUpdatingEmail}
      />

      <ConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title={t('CVReception.confirmTitle')}
        description={t('CVReception.confirmDescription')}
        onConfirm={() => setShowConfirmDialog(false)}
        onCancel={onContinue}
        confirmLabel={t('CVReception.confirmChooseReception')}
        cancelLabel={t('CVReception.confirmContinueWithoutCv')}
        cancelVariant="secondary"
      />
    </MainLayout>
  );
}
